import { log } from './Debug';

const DEFAULT_FFT_SIZE = 1024; // [32, 64, 128, 256, 512, 1024, 2048]
const TOTAL_POINTS = DEFAULT_FFT_SIZE / 2;
const BUBBLE_AVG_COLOR = 'rgba(29, 36, 57, 0.1)'; // dark blue
const BUBBLE_AVG_COLOR_2 = 'rgba(29, 36, 57, 0.05)';
const BUBBLE_AVG_LINE_COLOR = 'rgba(255, 255, 255, 1)';
const BUBBLE_AVG_LINE_COLOR_2 = 'rgba(77, 218, 248, 1)';
const AVG_BREAK_POINT = 100;
const SHOW_AVERAGE = true;
const PI = Math.PI;
const PI_TWO = PI * 2;
const DEG_TO_RAD = PI / 180; // 1 degree in radians
const RADIUS_DIVISOR = 5;

// Fallback for older Safari
const AudioCtx: typeof window.AudioContext =
  (window as any).AudioContext || (window as any).webkitAudioContext;

export interface AudioVisualizerOptions {
  width?: number; // logical canvas width (device-independent); doubled internally
  height?: number; // logical canvas height (device-independent); doubled internally
  elementId?: string; // canvas selector; defaults to '#ai-assistant-canvas'
  onAudioEnd?: () => void; // callback when audio finishes
  fftSize?: number; // analyser size
}

export class AudioVisualizer {
  private width: number;
  private height: number;
  private elementId?: string;
  private onAudioEnd?: () => void;
  private fftSize: number;

  private w = 0;
  private h = 0;
  private cx = 0;
  private cy = 0;

  private ctx!: CanvasRenderingContext2D;
  private actx!: AudioContext;
  private analyser!: AnalyserNode;
  private gainNode!: GainNode;
  private asource: AudioBufferSourceNode | null = null;

  private audioBuffer: AudioBuffer | null = null;
  private frequencyData!: Uint8Array;
  private timeData!: Uint8Array;
  private frequencyDataLength = 0;

  private playing = false;
  private startedAt = 0;
  private pausedAt = 0; // ms offset
  private avg = 0;
  private avgRadius = 0;
  private avgBreakPointHit = false;

  private requestAnimationFrameId: number | null = null;

  private points: {
    index: number;
    radius: number;
    angle: number;
    value: number;
    x: number;
    y: number;
    dx: number;
    dy: number;
  }[] = [];

  constructor(opts: AudioVisualizerOptions = {}) {
    this.width = opts.width ?? 128;
    this.height = opts.height ?? 128;
    this.elementId = opts.elementId;
    this.onAudioEnd = opts.onAudioEnd;
    this.fftSize = opts.fftSize ?? DEFAULT_FFT_SIZE;

    if (!AudioCtx) {
      log('audio playback is not supported');
    }
  }

  /** Prepare canvas and audio nodes — call before load/play. */
  public init(): void {
    const w = (this.w = (this.width || 128) * 2);
    const h = (this.h = (this.height || 128) * 2);

    this.cx = w / 2;
    this.cy = h / 2;

    // Always set up AudioContext first so audio plays even without a canvas
    if (!this.actx || this.actx.state === 'closed') {
      this.actx = new AudioCtx();
      // Auto-resume when camera/mic hardware causes AudioContext suspension
      // (e.g. when Zoom/WebRTC starts video, some browsers briefly suspend contexts)
      this.actx.addEventListener('statechange', () => {
        log('[audio] AudioContext statechange ->', this.actx?.state);
        if (this.actx?.state === 'suspended') {
          log('[audio] AudioContext suspended — attempting resume');
          this.actx.resume().catch(() => {});
        } else if (this.actx?.state === 'closed' && this.playing) {
          // Context was closed externally (e.g. browser closed it when media
          // hardware was activated by Zoom SDK). Our stop()/destroy() always
          // sets this.playing = false BEFORE calling asource.stop(), so if
          // this.playing is still true here, the close was not initiated by us.
          // Fire onAudioEnd so Provider.tsx can clean up isAudioPlaybackActiveRef.
          log('[audio] AudioContext closed externally while playing — firing onAudioEnd');
          this.playing = false;
          this.onAudioEnd?.();
        }
      });
    }

    // Create nodes (wired as: source -> gain -> analyser -> destination)
    this.gainNode = this.actx.createGain();
    this.gainNode.gain.value = 1;

    this.analyser = this.actx.createAnalyser();
    this.analyser.fftSize = this.fftSize;
    this.analyser.minDecibels = -100;
    this.analyser.maxDecibels = -30;
    this.analyser.smoothingTimeConstant = 0.8;

    this.gainNode.connect(this.analyser);
    this.analyser.connect(this.actx.destination);

    this.frequencyDataLength = this.analyser.frequencyBinCount;
    this.frequencyData = new Uint8Array(this.frequencyDataLength);
    this.timeData = new Uint8Array(this.frequencyDataLength);

    const canvasElement = document.querySelector(
      this.elementId || '#ai-assistant-canvas',
    ) as HTMLCanvasElement | null;

    if (!canvasElement) {
      console.warn(
        `AudioVisualizer: canvas element not found for selector "${
          this.elementId || '#ai-assistant-canvas'
        }", skipping visualization`,
      );
      return;
    }

    const ctx = canvasElement.getContext('2d');

    if (!ctx) {
      console.warn('AudioVisualizer: cannot get 2D context, skipping visualization');
      return;
    }

    this.ctx = ctx;

    this.resizeCanvas();
    this.createPoints();
  }

  /** Load an ArrayBuffer of audio into the buffer (does not auto-play). */
  public async load(stream: ArrayBuffer) {
    if (!this.actx) this.init();
    // Safari requires user gesture resume; attempt resume if suspended
    if (this.actx.state === 'suspended') {
      try {
        await this.actx.resume();
      } catch {
        /* noop */
      }
    }

    // decodeAudioData supports promises in modern browsers, but to be safe
    // we wrap callback-style in a Promise.
    this.audioBuffer = await new Promise<AudioBuffer>((resolve, reject) => {
      this.actx.decodeAudioData(
        stream.slice(0),
        (buffer) => resolve(buffer),
        (e) => reject(e),
      );
    });
  }

  /** Start or resume playback and visualization. */
  public play() {
    if (!this.audioBuffer) {
      log('AudioVisualizer.play called before load; nothing to play');
      return;
    }

    this.playing = true;
    this.startedAt = Date.now();

    if (this.pausedAt) {
      this.startedAt = Date.now() - this.pausedAt;
    }

    // Ensure AudioContext is running before starting (it may be suspended
    // if camera/mic hardware was activated between load() and play())
    if (this.actx.state === 'suspended') {
      this.actx.resume().catch(() => {});
    }

    // Create a new buffer source each time we (re)start
    this.asource = this.actx.createBufferSource();
    this.asource.buffer = this.audioBuffer;
    this.asource.loop = false;
    this.asource.addEventListener('ended', () => this.onEnded());
    this.asource.connect(this.gainNode);

    if (this.pausedAt) {
      this.asource.start(0, this.pausedAt / 1000);
    } else {
      this.asource.start();
    }

    this.animate();
  }

  /** Pause playback, retaining the offset. */
  public pause() {
    if (!this.playing || !this.asource) {
      return;
    }

    this.playing = false;
    this.pausedAt = Date.now() - this.startedAt;

    try {
      this.asource.stop();
    } catch {}

    this.asource = null;
    this.cancelAnim();
  }

  /** Stop playback and reset state (keeps decoded buffer). */
  public stop() {
    this.playing = false;
    this.pausedAt = 0;

    try {
      this.asource?.stop();
    } catch {}

    this.asource = null;
    this.cancelAnim();
    this.clearCanvas();
  }

  /** Fully tear down the visualizer and free resources. */
  public async destroy() {
    this.stop();

    if (this.actx && this.actx.state !== 'closed') {
      try {
        await this.actx.close();
      } catch {}
    }
  }

  /** Adjust output volume 0..1 */
  public setGain(value: number): void {
    if (!this.gainNode) {
      return;
    }
    this.gainNode.gain.value = Math.max(0, Math.min(1, value));
  }

  private onEnded() {
    log('audio ended');
    this.playing = false;
    this.pausedAt = 0;
    this.cancelAnim();
    this.clearCanvas();

    // callback on audio end playback
    if (this.onAudioEnd) {
      this.onAudioEnd();
    }
  }

  private animate = (): void => {
    if (!this.playing) {
      return;
    }

    this.requestAnimationFrameId = window.requestAnimationFrame(this.animate);

    this.analyser.getByteFrequencyData(this.frequencyData);
    this.analyser.getByteTimeDomainData(this.timeData);

    this.avg =
      this.getAvg(Array.from(this.frequencyData)) * this.gainNode.gain.value;
    this.avgBreakPointHit = this.avg > AVG_BREAK_POINT;

    this.clearCanvas();

    if (SHOW_AVERAGE) {
      this.drawAverageCircle();
      // Optionally draw avatar into canvas — intentionally disabled due to quality
      // const avatar = document.querySelector('#assistant-avatar') as HTMLImageElement | null;
      // if (avatar) this.ctx.drawImage(avatar, 0, 0, 2048, 2048, 32, 32, 64, 64);
    }
  };

  private cancelAnim() {
    if (this.requestAnimationFrameId !== null) {
      cancelAnimationFrame(this.requestAnimationFrameId);
      this.requestAnimationFrameId = null;
    }
  }

  private getAvg(values: number[]): number {
    if (!values.length) return 0;
    let sum = 0;
    for (let i = 0; i < values.length; i++) sum += values[i];
    return sum / values.length;
  }

  private clearCanvas() {
    if (!this.ctx) return;
    this.ctx.beginPath();
    this.ctx.globalCompositeOperation = 'source-over';
    this.ctx.clearRect(0, 0, this.w, this.h);
  }

  private drawAverageCircle() {
    if (!this.ctx) return;
    this.ctx.strokeStyle = this.avgBreakPointHit
      ? BUBBLE_AVG_LINE_COLOR_2
      : BUBBLE_AVG_LINE_COLOR;
    this.ctx.fillStyle = this.avgBreakPointHit
      ? BUBBLE_AVG_COLOR_2
      : BUBBLE_AVG_COLOR;

    this.ctx.beginPath();
    this.ctx.lineWidth = 2; // circle line width
    this.ctx.arc(this.cx, this.cy, this.avg + this.avgRadius, 0, PI_TWO, false);
    this.ctx.stroke();
    this.ctx.fill();
    this.ctx.closePath();
  }

  private baseRadius(): number {
    return Math.max(this.w, this.h) / RADIUS_DIVISOR;
  }

  private createPoints() {
    this.points = [];

    for (let i = 0; i < TOTAL_POINTS; i++) {
      this.points.push(this.makePoint(i + 1));
    }

    this.avgRadius = this.baseRadius();
  }

  private makePoint(index: number) {
    const radius = this.baseRadius();
    const angleDeg = (index * 360) / TOTAL_POINTS;
    const angle = DEG_TO_RAD * angleDeg;
    const value = Math.random() * 256;
    const x = this.cx + radius * Math.sin(angle);
    const y = this.cy + radius * Math.cos(angle);

    return {
      index,
      radius,
      angle: angleDeg,
      value,
      x,
      y,
      dx: x + value * Math.sin(angle),
      dy: y + value * Math.cos(angle),
    };
  }

  private resizeCanvas() {
    if (!this.ctx) return;
    const canvas = this.ctx.canvas;
    const base = this.baseRadius();

    canvas.width = this.w;
    canvas.height = this.h;

    this.points = this.points.map((p) => {
      const angle = DEG_TO_RAD * p.angle;

      return {
        ...p,
        radius: base,
        x: this.cx + base * Math.sin(angle),
        y: this.cy + base * Math.cos(angle),
      };
    });
  }

  public static toArrayBuffer(audio: Record<string, number>) {
    const uint8Array = Uint8Array.from(Object.values(audio));
    const arrayBuffer = uint8Array.buffer;

    return arrayBuffer;
  }
}

// Backward compatible visualizer code
export const visualizeAudio = (
  width = 128,
  height = 128,
  onAudioEndCallback: Function,
  elementId?: string,
) => {
  const visualizerInstance = new AudioVisualizer({
    width,
    height,
    elementId,
    onAudioEnd: () => {
      try {
        onAudioEndCallback && (onAudioEndCallback as () => void)();
      } catch {}
    },
  });

  return {
    initialize: async (stream: ArrayBuffer) => {
      visualizerInstance.init();
      await visualizerInstance.load(stream);
      visualizerInstance.play();
    },
    stopAudio: () => visualizerInstance.destroy(),
  };
};
